import { useState, useEffect, useRef } from 'react';
import { Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import FacebookBusinessLoginButton from '../Facebook/loginButton';
import ModalOtp from './ModalOtp';
import caAPI from '../../../services/caAPI';
import { successToast, errorToast, infoToast } from '../../../components/DommusToast';

const LABELS_TOKENS = {
  api_key: 'API Key',
  account_sid: 'Account SID',
  secret_auth_token: 'Auth Token',
  twiml_application_sid: 'TwiML Application SID',
  api_secret: 'API Secret',
};

export default function WhatsApp() {
  const [openModalOtp, setOpenModalOtp] = useState(false);
  const [senderSid, setSenderSid] = useState('');
  const [sdkTimeout, setSdkTimeout] = useState(false);
  const [diagnostico, setDiagnostico] = useState(null); // null = carregando
  const [accountSidEscolhido, setAccountSidEscolhido] = useState('');
  const [nameStatus, setNameStatus] = useState(null); // status de certificação do nome resolvido pela Meta
  const timerRef = useRef(null);

  const idInquilino = caAPI.getLocalState('idInquilino');

  useEffect(() => {
    caAPI.whatsapp_sender.diagnosticarConfiguracao(idInquilino)
      .then((res) => setDiagnostico(res.data))
      .catch(() => setDiagnostico({ erro: true }));
  }, [idInquilino]);

  // O code expira em 30s — o back faz a troca imediatamente ao receber.
  const codeRef = useRef(null);
  const dadosSignupRef = useRef(null); // { phone_number_id, waba_id } — do postMessage
  const enviandoRef = useRef(false);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (!window.FB) {
        setSdkTimeout(true);
        errorToast.fire({ text: 'Falha ao carregar o SDK do Facebook.' });
      }
    }, 10000);

    return () => clearTimeout(timerRef.current);
  }, []);

  function tentarEnviar() {
    if (enviandoRef.current) return;
    if (!dadosSignupRef.current || !codeRef.current) return;

    enviandoRef.current = true;
    const { phone_number_id, waba_id } = dadosSignupRef.current;
    const code = codeRef.current;

    // profile_name não é mais digitado: o nome vem do verified_name da Meta (resolvido no CCD).
    const payload = { waba_id, phone_number_id, code };
    if (accountSidEscolhido) {
      payload.account_sid = accountSidEscolhido;
    }

    trackPromise(
      caAPI.whatsapp_sender.registrar(payload)
        .then((res) => {
          const { status, sender_sid } = res.data;
          if (status === 'PENDING') {
            setSenderSid(sender_sid);
            setOpenModalOtp(true);
          } else if (status === 'ONLINE' || res.data.sucesso) {
            setNameStatus(res.data.name_status || null);
            successToast.fire({ text: 'Número WhatsApp registrado com sucesso!' });
          }
        })
        .catch((err) => {
          enviandoRef.current = false;
          const status = err.response?.status;
          const mensagem =
            err.response?.data?.errors?.[0]?.message ||
            err.response?.data?.message ||
            'Erro ao registrar sender.';
          if (status === 409) {
            Swal.fire({ icon: 'warning', titleText: 'Atenção', text: mensagem });
          } else {
            Swal.fire({ icon: 'error', titleText: 'Erro', text: mensagem });
          }
        })
    );
  }

  function handleEmbeddedSignupEvent(data) {
    if (data.event === 'FINISH') {
      const { phone_number_id, waba_id } = data.data;

      if (!phone_number_id || !waba_id) {
        Swal.fire({ icon: 'error', titleText: 'Erro', text: 'Não foi possível obter os dados do número WhatsApp. Tente novamente.' });
        return;
      }

      // Nome de exibição resolvido automaticamente pelo verified_name da Meta — sem digitação.
      dadosSignupRef.current = { phone_number_id, waba_id };
      tentarEnviar();
    } else if (data.event === 'CANCEL') {
      infoToast.fire({ text: 'Fluxo de conexão cancelado.' });
    }
  }

  function handleSuccess(authResponse) {
    if (timerRef.current) clearTimeout(timerRef.current);
    codeRef.current = authResponse.code;
    tentarEnviar();
  }

  function handleError() {
    if (timerRef.current) clearTimeout(timerRef.current);
    infoToast.fire({ text: 'Fluxo de conexão cancelado.' });
  }

  function handleConfirmarOtp(codigo) {
    const payload = { sender_sid: senderSid, codigo };
    if (accountSidEscolhido) {
      payload.account_sid = accountSidEscolhido;
    }
    trackPromise(
      caAPI.whatsapp_sender.verificarOtp(payload)
        .then((res) => {
          setOpenModalOtp(false);
          setNameStatus(res.data?.name_status || null);
          successToast.fire({ text: 'Número WhatsApp registrado com sucesso!' });
        })
        .catch((err) => {
          Swal.fire({
            icon: 'error',
            titleText: 'Erro',
            text:
              err.response?.data?.errors?.[0]?.message ||
              err.response?.data?.message ||
              'Erro ao verificar código OTP.',
          });
        })
    );
  }

  function handleFecharModalOtp() {
    setOpenModalOtp(false);
  }

  // ponytail: extras deve ser passado como objeto { extras: {...} } ao FB.login,
  // não espalhado no nível raiz — o solutionID dentro de extras.setup é o que
  // instrui o Meta a compartilhar a WABA com a Twilio (causa do erro 63101 se ausente).
  // sessionInfoVersion: 3 é obrigatório para receber waba_id no postMessage.
  const extras = {
    auth_type: 'rerequest', // evita erro "user already logged in" em tentativas repetidas
    extras: {
      sessionInfoVersion: 3, // versão 3 obrigatória para receber waba_id
      featureType: 'only_waba_sharing',
      setup: { solutionID: process.env.REACT_APP_TWILIO_SOLUTION_ID },
    },
  };

  const configuracaoInvalida = diagnostico && (
    diagnostico.erro ||
    !diagnostico.inquilino_cadastrado ||
    !diagnostico.tokens_cadastrados
  );

  const accountSids = (diagnostico && diagnostico.account_sids) || [];
  const exibirSeletorAccountSid = accountSids.length >= 2;
  // Com 2+ SIDs, a escolha é obrigatória antes de disparar o registro.
  const escolhaAccountSidPendente = exibirSeletorAccountSid && !accountSidEscolhido;

  function renderizarSeletorAccountSid() {
    if (configuracaoInvalida || !exibirSeletorAccountSid) return null;

    return (
      <div className="seletor-account-sid mt-2 mb-2">
        <label htmlFor="account-sid">Conta Twilio (Account SID)</label>
        <select
          id="account-sid"
          name="account-sid"
          className="form-control"
          value={accountSidEscolhido}
          onChange={(evento) => setAccountSidEscolhido(evento.target.value)}
        >
          <option value="">Selecione a conta Twilio</option>
          {accountSids.map((sid) => (
            <option key={sid} value={sid}>{sid}</option>
          ))}
        </select>
      </div>
    );
  }

  function renderizarAlertDiagnostico() {
    if (!configuracaoInvalida) return null;

    if (diagnostico.erro) {
      return (
        <Alert variant="danger" className="mt-2">
          Não foi possível verificar a configuração Twilio. Tente novamente mais tarde.
        </Alert>
      );
    }

    if (!diagnostico.inquilino_cadastrado) {
      return (
        <Alert variant="warning" className="mt-2">
          Este inquilino não está cadastrado no sistema de comunicação. Entre em contato com o suporte.
        </Alert>
      );
    }

    const tokensAusentes = (diagnostico.tokens_ausentes || [])
      .map((t) => LABELS_TOKENS[t] || t)
      .join(', ');

    return (
      <Alert variant="warning" className="mt-2">
        Configuração Twilio incompleta. Token(s) ausente(s): <strong>{tokensAusentes}</strong>.
        Entre em contato com o suporte para configurar a integração.
      </Alert>
    );
  }

  function renderizarAlertNameStatus() {
    // APPROVED ou vazio → sem alerta.
    if (!nameStatus || nameStatus === 'APPROVED') return null;

    if (nameStatus === 'DECLINED' || nameStatus === 'EXPIRED') {
      return (
        <Alert variant="danger" className="mt-2">
          O nome de exibição do perfil WhatsApp está com status <strong>{nameStatus}</strong>.
          Ajuste o nome de exibição no WhatsApp Business Manager da Meta.
        </Alert>
      );
    }

    // PENDING_REVIEW, AVAILABLE_WITHOUT_REVIEW, NONE e demais → aviso.
    return (
      <Alert variant="warning" className="mt-2">
        O nome de exibição do perfil WhatsApp está com status <strong>{nameStatus}</strong>.
        Acompanhe a situação no WhatsApp Business Manager da Meta.
      </Alert>
    );
  }

  return (
    <>
      <Card.Body>
        <Card.Title>
          <FontAwesomeIcon icon={faWhatsapp} color="#25D366" size="2x" />
        </Card.Title>
        <Card.Text>Mensagens</Card.Text>
        {renderizarAlertDiagnostico()}
        {renderizarAlertNameStatus()}
        {renderizarSeletorAccountSid()}
      </Card.Body>
      {!configuracaoInvalida && (
        <FacebookBusinessLoginButton
          appId={process.env.REACT_APP_META_APP_ID}
          version={process.env.REACT_APP_META_SDK_VERSION}
          configId={process.env.REACT_APP_META_WA_CONFIG_ID}
          extras={extras}
          onSuccess={handleSuccess}
          onEmbeddedSignupEvent={handleEmbeddedSignupEvent}
          onError={handleError}
          disabled={sdkTimeout || diagnostico === null || escolhaAccountSidPendente}
        >
          Conectar WhatsApp
        </FacebookBusinessLoginButton>
      )}
      <ModalOtp
        open={openModalOtp}
        onClose={handleFecharModalOtp}
        onConfirmar={handleConfirmarOtp}
      />
    </>
  );
}
